import { PropsWithChildren, useMemo, useRef } from 'react'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import { SanityBlock } from 'sanity-codegen'

import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { HeroTitleTypography } from 'src/components/layouts/HeroRebrand/HeroTitleTypography'
import { ProjectCarouselRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'

type CarouselRebrandProps = PropsWithChildren<{
  title?: SanityBlock[] | string
  description?: string
  backgroundColor?: ProjectCarouselRebrandResult['backgroundColor']
}>

const BACKGROUND_COLOR_CLASSES: Record<
  NonNullable<ProjectCarouselRebrandResult['backgroundColor']>,
  string
> = {
  linen: 'bg-linen text-forestDeep',
  wineRich: 'bg-wineRich text-cream',
  forestDeep: 'bg-forestDeep text-linen',
  lavenderPale: 'bg-lavenderPale text-forestDeep',
  limePale: 'bg-limePale text-forestDeep'
}

const CarouselRebrand = ({
  children,
  title,
  description,
  backgroundColor
}: CarouselRebrandProps) => {
  const carouselRef = useRef<HTMLDivElement>(null)
  const currentIndex = useRef(0)

  const hasSingleCard = useMemo(() => {
    if (Array.isArray(children)) {
      return children.length === 1
    }
    return true
  }, [children])

  const scrollCarousel = (direction: 'next' | 'previous') => {
    if (!carouselRef.current) {
      return
    }

    const carousel = carouselRef.current
    const cards = Array.from(carousel.children) as HTMLElement[]
    if (cards.length === 0) {
      return
    }

    const targetIndex =
      direction === 'next'
        ? Math.min(currentIndex.current + 1, cards.length - 1)
        : Math.max(currentIndex.current - 1, 0)

    const targetCard = cards[targetIndex]
    if (!targetCard) {
      return
    }

    currentIndex.current = targetIndex
    targetCard.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest'
    })
  }

  const navButtonClassNames = useMemo(() => {
    switch (backgroundColor) {
      case 'linen':
      case 'lavenderPale':
      case 'limePale':
        return 'border-forestDeep text-forestDeep hover:bg-forestDeep hover:text-white'
      case 'wineRich':
        return 'border-cream text-cream hover:bg-cream hover:text-wineRich'
      case 'forestDeep':
        return 'border-linen text-linen hover:bg-linen hover:text-forestDeep'
      default:
        return 'border-forestDeep text-forestDeep hover:bg-forestDeep hover:text-white'
    }
  }, [backgroundColor])

  return (
    <section
      className={`flex w-full flex-col gap-10 overflow-hidden ${
        backgroundColor ? BACKGROUND_COLOR_CLASSES[backgroundColor] : ''
      }`}
    >
      <div className='flex flex-col gap-8 container mx-auto px-4 py-12 lg:px-20 lg:py-18'>
        <div className='flex items-end justify-between gap-8'>
          <div className='flex max-w-3xl flex-col gap-2 items-start'>
            {title ? <HeroTitleTypography variant='h2' title={title} /> : null}
            {description ? (
              <div className='max-w-120 w-full'>
                <TypographyRebrand variant='paragraph'>
                  {description}
                </TypographyRebrand>
              </div>
            ) : null}
          </div>
          {hasSingleCard ? null : (
            <div className='flex shrink-0 gap-3'>
              <button
                type='button'
                aria-label='Previous article'
                className={`cursor-pointer flex h-10 w-10 items-center justify-center rounded-full border-2 ${navButtonClassNames}`}
                onClick={() => scrollCarousel('previous')}
              >
                <MdChevronLeft size={32} aria-hidden='true' />
              </button>
              <button
                type='button'
                aria-label='Next article'
                className={`cursor-pointer flex h-10 w-10 items-center justify-center rounded-full border-2 ${navButtonClassNames}`}
                onClick={() => scrollCarousel('next')}
              >
                <MdChevronRight size={32} aria-hidden='true' />
              </button>
            </div>
          )}
        </div>
        <div
          ref={carouselRef}
          className='scrollbar-none flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain pb-3'
        >
          {children}
        </div>
      </div>
    </section>
  )
}

export default CarouselRebrand
